import React, { useEffect, useState } from "react";
import { Copy, Loader2, RefreshCw, Send, CheckCircle2, PauseCircle, HelpCircle, ThumbsUp, ThumbsDown, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

const SURVEY = {
  release: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-500/10", key: "chatter_release" },
  hold: { icon: PauseCircle, cls: "bg-red-50 text-red-700 border-red-300 dark:bg-red-500/10", key: "chatter_hold" },
  unknown: { icon: HelpCircle, cls: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-500/10", key: "chatter_unknown" },
};

export const ChatterExpressDialog = ({ open, onOpenChange, jobId, date, jobLabel }) => {
  const { t, lang, user } = useApp();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("en");

  const load = async (force = false) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/daily-summaries/chatter", { user_id: user.id, job_id: jobId, date, force }, { timeout: 90000 });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(false); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const text = data ? (view === "pt" ? data.report_pt : data.report_en) : "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const startEdit = () => { setDraft(text); setEditing(true); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/daily-summaries/chatter/edit", { job_id: jobId, date, lang: view, text: draft });
      setData(d); setEditing(false); toast.success(t("chatter_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success(t("chatter_copied")); } catch (e) { toast.error(t("something_wrong")); }
  };
  const sv = SURVEY[data?.survey] || SURVEY.unknown;
  const SvIcon = sv.icon;
  const showThumbs = !!data; // supervisor can always override the AI's verdict (tap again to undo)
  const setSat = async (value) => {
    const next = data.satisfaction === value ? null : value;
    setBusy(true);
    try {
      const { data: d } = await api.post("/daily-summaries/chatter/satisfaction", { job_id: jobId, date, value: next });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="chatter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Chatter Express</DialogTitle>
          <DialogDescription>{jobLabel} · {date}<br />{t("chatter_hint")}</DialogDescription>
        </DialogHeader>
        {busy && !data && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center" data-testid="chatter-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> GPT-4o…
          </div>
        )}
        {data && (
          <>
            <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold ${sv.cls}`} data-testid={`chatter-survey-${data.survey}`}>
              <SvIcon className="h-4 w-4 shrink-0" />
              <span className="flex-1 min-w-0">{t(sv.key)}</span>
              {showThumbs && (
                <div className="flex items-center gap-1.5 shrink-0" data-testid="chatter-thumbs">
                  <button type="button" data-testid="chatter-thumb-up" title={t("chatter_thumb_up")} aria-label={t("chatter_thumb_up")}
                    disabled={busy} onClick={() => setSat("happy")}
                    className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-colors ${data.satisfaction === "happy" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:bg-transparent"}`}>
                    <ThumbsUp className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid="chatter-thumb-down" title={t("chatter_thumb_down")} aria-label={t("chatter_thumb_down")}
                    disabled={busy} onClick={() => setSat("unhappy")}
                    className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-colors ${data.satisfaction === "unhappy" ? "bg-red-600 border-red-600 text-white" : "bg-white border-red-500 text-red-600 hover:bg-red-50 dark:bg-transparent"}`}>
                    <ThumbsDown className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {showThumbs && !data.satisfaction && data.survey === "unknown" && (
              <p className="text-xs text-muted-foreground -mt-1" data-testid="chatter-sat-hint">{t("chatter_sat_hint")}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border p-0.5 text-xs">
                {["en", "pt"].map((v) => (
                  <button key={v} type="button" data-testid={`chatter-view-${v}`} onClick={() => setView(v)}
                    className={`px-3 py-1 rounded-md font-semibold ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                    {v.toUpperCase()}
                  </button>
                ))}
              </div>
              <span className="text-[11px] text-muted-foreground ml-auto whitespace-nowrap">{t("chatter_generated")} {new Date(data.generated_at).toLocaleTimeString(lang === "pt" ? "pt-BR" : lang === "es" ? "es" : "en-US", { hour: "2-digit", minute: "2-digit" })}</span>
              <Button size="sm" variant="outline" className="h-8" data-testid="chatter-regen" disabled={busy || editing} onClick={() => load(true)}>
                <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
              </Button>
              {editing ? (
                <>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0" data-testid="chatter-edit-cancel" title={t("cancel")} aria-label={t("cancel")} disabled={busy} onClick={() => setEditing(false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" className="h-8 w-8 p-0" data-testid="chatter-edit-save" title={t("save")} aria-label={t("save")} disabled={busy || !draft.trim()} onClick={saveEdit}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" className="h-8 w-8 p-0" data-testid="chatter-edit" title={t("edit")} aria-label={t("edit")} disabled={busy} onClick={startEdit}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" className="h-8" data-testid="chatter-copy" onClick={copy}>
                    <Copy className="h-3.5 w-3.5 mr-1" /> {t("chatter_copy")}
                  </Button>
                </>
              )}
            </div>
            {editing ? (
              <textarea data-testid="chatter-edit-textarea" value={draft} onChange={(e) => setDraft(e.target.value)} rows={Math.min(24, Math.max(8, draft.split("\n").length + 1))}
                className="w-full whitespace-pre-wrap text-sm leading-relaxed font-sans rounded-xl bg-background p-3 border border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y" />
            ) : (
              <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans rounded-xl bg-muted/50 p-3 border" data-testid="chatter-text">{text}</pre>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
